import { create } from '@bufbuild/protobuf';
import type { Date as ProtoDate } from '@/gen/google/type/date_pb';
import { DateSchema } from '@/gen/google/type/date_pb';

/**
 * Convert JavaScript Date to google.type.Date proto message
 * @param date - JavaScript Date object
 * @returns google.type.Date object with year, month, day fields
 * @throws Error if date is null, undefined, or invalid
 */
export function toProtoDate(date: Date | null | undefined): ProtoDate {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("Ngày không hợp lệ được truyền vào toProtoDate");
  }

  return create(DateSchema, {
    year: date.getFullYear(),
    month: date.getMonth() + 1, // JavaScript month is 0-indexed, proto month is 1-indexed
    day: date.getDate(),
  });
}

/**
 * Convert google.type.Date proto message to JavaScript Date
 * @param protoDate - google.type.Date object (can be undefined)
 * @returns JavaScript Date object, or null if protoDate is undefined
 */
export function fromProtoDate(protoDate: ProtoDate | undefined): Date | null {
  if (!protoDate) {
    return null;
  }

  // Create Date with year, month (0-indexed), day
  return new Date(protoDate.year, protoDate.month - 1, protoDate.day);
}

/**
 * Formats a Date as YYYY-MM-DD from its local calendar day.
 *
 * `toISOString()` is not used: it converts to UTC first, so a date picked in
 * Vietnam reads as the previous day for anyone whose offset pushes it back
 * across midnight - and a survey's start and end dates sit exactly there.
 *
 * @param date - The date to format.
 * @returns The formatted date, or "" when there is no valid date.
 */
export function toIsoDate(date: Date | null | undefined): string {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Parses YYYY-MM-DD into a Date at local midnight.
 *
 * `new Date("2026-09-01")` parses as UTC midnight, which lands on the previous
 * day in western time zones. Building from the parts keeps the calendar day
 * the one that was written.
 *
 * A date that does not exist - "2026-02-31" - is rejected rather than allowed
 * to roll over into March, which is what the Date constructor would do.
 *
 * @param value - The date string to parse.
 * @returns The parsed date, or null when the string is not a real date.
 */
export function fromIsoDate(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? "").trim());
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));

  // Reject a rolled-over date by checking the round trip.
  return toIsoDate(date) === `${year}-${month}-${day}` ? date : null;
}

/**
 * Shifts a YYYY-MM-DD date by a whole number of days.
 *
 * The arithmetic runs in UTC so that no local daylight-saving transition can
 * shorten a day and move the result onto the wrong date.
 *
 * @param date - The date to shift, as YYYY-MM-DD.
 * @param days - Days to add; negative values move backwards.
 * @returns The shifted date, as YYYY-MM-DD.
 *
 * @example
 *   shiftIsoDate("2026-09-14", -6) // "2026-09-08"
 */
export function shiftIsoDate(date: string, days: number): string {
  const shifted = new Date(`${date}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/**
 * The Vietnam calendar date it is right now, as YYYY-MM-DD.
 *
 * The browser's own date is not interchangeable with this: Vietnam is UTC+7,
 * so for any viewer west of it the two disagree for part of every day. Reports
 * bucketed by Vietnam midnight must be anchored to this, not to the viewer's
 * clock.
 *
 * @returns Today's Vietnam date, as YYYY-MM-DD.
 */
export function vietnamToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * Formats YYYY-MM-DD as DD/MM for chart axis labels.
 *
 * @param iso - A YYYY-MM-DD day.
 * @returns The day as DD/MM, or the input unchanged if it is not an ISO day.
 */
export function formatDayMonth(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}/${match[2]}` : iso;
}

/**
 * Formats YYYY-MM-DD as DD/MM/YYYY for display.
 *
 * @param iso - A YYYY-MM-DD day.
 * @returns The day as DD/MM/YYYY, or the input unchanged if it is not an ISO day.
 */
export function formatDayMonthYear(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}
